import type { DictationSegment } from "./types";

export const DEEPGRAM_LISTEN_URL = "wss://api.deepgram.com/v1/listen";

/**
 * Streaming query for raw 16-bit PCM at the capture rate. Deepgram requires `encoding` and
 * `sample_rate` for headerless audio; `smart_format` adds punctuation and number formatting.
 */
export function buildListenUrl(options: { sampleRate: number; language?: string }): string {
  const query = new URLSearchParams({
    model: "nova-3",
    language: options.language ?? "en",
    smart_format: "true",
    interim_results: "true",
    encoding: "linear16",
    sample_rate: String(Math.round(options.sampleRate)),
    channels: "1",
  });
  return `${DEEPGRAM_LISTEN_URL}?${query}`;
}

/** Temporary tokens ride in the WebSocket subprotocol; browsers cannot set an Authorization header. */
export function socketProtocols(token: string): string[] {
  return ["bearer", token];
}

export const CLOSE_STREAM = JSON.stringify({ type: "CloseStream" });

export type DeepgramEvent =
  | { type: "segment"; segment: DictationSegment }
  | { type: "error"; message: string };

/**
 * Parses one text frame. Returns `null` for frames that carry nothing to act on: metadata,
 * utterance boundaries, and empty finals (Deepgram emits one at each silence gap).
 */
export function parseDeepgramMessage(raw: unknown): DeepgramEvent | null {
  if (typeof raw !== "string") return null;
  let message: unknown;
  try {
    message = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!message || typeof message !== "object") return null;
  const record = message as Record<string, unknown>;
  if (record["type"] === "Error" || typeof record["err_code"] === "string") {
    const text = record["err_msg"] ?? record["message"] ?? record["description"];
    return { type: "error", message: typeof text === "string" ? text : "Deepgram error" };
  }
  if (record["type"] !== "Results") return null;
  const channel = record["channel"] as
    | { alternatives?: Array<{ transcript?: unknown }> }
    | undefined;
  const transcript = channel?.alternatives?.[0]?.transcript;
  const text = typeof transcript === "string" ? transcript : "";
  const isFinal = record["is_final"] === true;
  if (isFinal && !text.trim()) return null;
  return { type: "segment", segment: { text, isFinal } };
}
